import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "react-bootstrap";

import { X, CheckIcon, ProhibitIcon } from "@phosphor-icons/react";

import Icon from "components/Icon/Icon";
import { CampaignReportStyles } from "./styled";
import Button from "components/Buttons";
import { useMarketplaceSlice } from "../slice";
import { selectCampaignReport } from "../slice/selector";
import SwitchInput from "components/Toggles/Switch/index.jsx";

const CampaignReportModal = ({
  show,
  onHide,
  rowData,
  poster_counts,
  posterCountsLoading
}) => {
  const dispatch = useDispatch();
  const { actions } = useMarketplaceSlice();
  const { updateLoading, updateSuccess } = useSelector(selectCampaignReport);

  const [formData, setFormData] = useState({
    is_hidden: 0
  });

  // pre-fill when rowData changes
  useEffect(() => {
    if (rowData?.id) {
      setFormData({
        is_hidden: rowData?.marketplace_request?.is_hidden || 0
      });
    }
  }, [rowData?.id]);

  // close modal after success
  useEffect(() => {
    if (updateSuccess) {
      onHide();
      dispatch(actions.resetUpdateSuccess());
    }
  }, [updateSuccess]);

  const isHideChanged =
    formData.is_hidden !== (rowData?.marketplace_request?.is_hidden || 0);

  // hide switch enabled -> update status enabled, ignore disabled
  // hide switch not changed -> update status disabled, ignore enabled
  const isUpdateEnabled = isHideChanged;
  const isIgnoreEnabled = !isHideChanged;

  const handleUpdate = () => {
    dispatch(
      actions.updateCampaignReportStatus({
        reportId: rowData?.id,
        report_status: "hidden",
        is_hidden: formData.is_hidden
      })
    );
  };

  const handleIgnore = () => {
    dispatch(
      actions.updateCampaignReportStatus({
        reportId: rowData?.id,
        report_status: "ignored",
        is_hidden: formData.is_hidden
      })
    );
  };

  return (
    <CampaignReportStyles
      show={show}
      onHide={onHide}
      className="sidebar-modal"
      backdrop
      keyboard
      unmountOnExit
    >
      <Modal.Header>
        <Modal.Title>Campaigns Reported as Issues by Creators</Modal.Title>
        <div onClick={onHide} className="close-icon-wrapper">
          <Icon component={X} size={20} />
        </div>
      </Modal.Header>

      <Modal.Body>
        <div className="company-details-wrapper">
          <Modal.Title>Campaign Details</Modal.Title>
          <div className="brand-name">
            <div className="logo-text">Created by</div>
            <p className="main-text">
              {[rowData?.user?.first_name, rowData?.user?.last_name]
                .filter(Boolean)
                .join(" ") || "-"}{" "}
            </p>
            <p className="main-text">{rowData?.user?.phone_number || "-"}</p>
          </div>

          <div className="brand-name ig_handle">
            <div className="logo-text">Campaign Link</div>
            <a
              className="main-text anchor-text"
              href={`https://hashfame.com/influencer-marketing-jobs/${rowData?.marketplace_request?.id_hash}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {`https://hashfame.com/influencer-marketing-jobs/${rowData?.marketplace_request?.id_hash}` ||
                "-"}
            </a>
          </div>

          <div className="brand-name">
            <div className="logo-text">
              Total campaigns created by {rowData?.user?.first_name || "-"}
            </div>
            <p className="main-text">
              {posterCountsLoading
                ? "-"
                : (poster_counts?.poster_total_campaigns ?? "-")}
            </p>
          </div>

          <div className="brand-name">
            <div className="logo-text">
              Total campaigns got reported by creators so far
            </div>
            <p className="main-text">
              {posterCountsLoading
                ? "-"
                : (poster_counts?.poster_total_reported_campaigns ?? "-")}
            </p>
          </div>
        </div>

        <div className="company-details-wrapper creator-details">
          <Modal.Title>Reported by creator&apos;s details</Modal.Title>
          <div className="brand-name">
            <div className="logo-text">Campaign Reported By Creator</div>
            <p className="main-text">
              {[
                rowData?.creator_user?.first_name,
                rowData?.creator_user?.last_name
              ]
                .filter(Boolean)
                .join(" ") || "-"}
            </p>
          </div>

          <div className="brand-name">
            <div className="logo-text">Creator&apos;s Instagram</div>
            {rowData?.creator_user?.creator?.profile?.ig_handle ? (
              <a
                className="main-text anchor-text"
                href={`https://www.instagram.com/${rowData.creator_user.creator.profile.ig_handle}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                @{rowData.creator_user.creator.profile.ig_handle}
              </a>
            ) : (
              <span className="main-text">-</span>
            )}
          </div>

          <div className="brand-name">
            <div className="logo-text">Report Reason</div>
            <p className="main-text">{rowData?.reason || "-"}</p>
          </div>
        </div>

        <div className="company-details-wrapper creator-details">
          <Modal.Title>Action</Modal.Title>
          <label className="hide-profile" htmlFor="hide-profile">
            <span>Hide Campaign</span>
            <SwitchInput
              id="hide-profile"
              checked={formData.is_hidden === 1}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  is_hidden: e.target.checked ? 1 : 0
                }))
              }
            />
          </label>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="button-wrapper">
          {/* Ignore button — enabled by default, disabled when hide switch is changed */}
          <Button
            variant="secondary"
            leftIcon={
              <Icon
                component={ProhibitIcon}
                format="outline"
                weight="regular"
                size={16}
              />
            }
            onClick={handleIgnore}
            disabled={updateLoading || !isIgnoreEnabled}
          >
            Ignore
          </Button>

          {/* Update Status button — enabled only when hide switch is toggled */}
          <Button
            leftIcon={
              <Icon
                component={CheckIcon}
                format="outline"
                weight="regular"
                size={16}
              />
            }
            onClick={handleUpdate}
            disabled={updateLoading || !isUpdateEnabled}
          >
            {updateLoading ? "Updating..." : "Update Status"}
          </Button>
        </div>

        <span className="warning-text">
          *The selected report status will be updated.
        </span>
      </Modal.Footer>
    </CampaignReportStyles>
  );
};

export default CampaignReportModal;
